/**
 * Pure date-range preset predicates for "intentional" table filters.
 *
 * Every helper works in the viewer's local time zone and computes month / year
 * boundaries with the `Date(year, monthIndex, day)` constructor, which rolls
 * over correctly at year edges (month `-1` is December of the previous year,
 * month `12` is January of the next one). Each predicate takes the value being
 * tested plus an optional `now` — the latter defaults to the current time and
 * exists so the helpers can be unit-tested deterministically.
 */

export type DateInput = string | number | Date | null | undefined;

/** Parses any accepted input into a valid `Date`, or `null` when absent/invalid. */
export function toDate(value: DateInput): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfMonth(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function startOfYear(now: Date): Date {
  return new Date(now.getFullYear(), 0, 1);
}

/** Value falls within the current calendar month. */
export function isThisMonth(value: DateInput, now: Date = new Date()): boolean {
  const date = toDate(value);
  if (!date) return false;
  const from = startOfMonth(now);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return date >= from && date < to;
}

/** Value falls within the previous calendar month. */
export function isLastMonth(value: DateInput, now: Date = new Date()): boolean {
  const date = toDate(value);
  if (!date) return false;
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = startOfMonth(now);
  return date >= from && date < to;
}

/** Value falls within the current calendar year. */
export function isThisYear(value: DateInput, now: Date = new Date()): boolean {
  const date = toDate(value);
  if (!date) return false;
  const from = startOfYear(now);
  const to = new Date(now.getFullYear() + 1, 0, 1);
  return date >= from && date < to;
}

/** Value is within the last 30×24h up to (and including) `now`. */
export function isLast30Days(
  value: DateInput,
  now: Date = new Date(),
): boolean {
  const date = toDate(value);
  if (!date) return false;
  const from = now.getTime() - 30 * 24 * 60 * 60 * 1000;
  return date.getTime() >= from && date.getTime() <= now.getTime();
}

/** Value exists but predates the current calendar year (i.e. it is "older"). */
export function isNotThisYear(
  value: DateInput,
  now: Date = new Date(),
): boolean {
  const date = toDate(value);
  if (!date) return false;
  return date < startOfYear(now);
}

/** No value at all (never active). */
export function isNever(value: DateInput): boolean {
  return toDate(value) === null;
}
